import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { BookSalesResponse } from "reedy-data/api";
import type { ChartConfig } from "components/ui/chart";
import {
  bundleFormatBgClass,
  bundleFormatColor,
  CHART_COLORS,
  chartSeriesBgClass,
  chartSeriesColor,
  GranularityToggle,
  MarketLegend,
  SalesBarChart,
  estimateFormatTooltipWidth,
} from "components/charts";
import type { Granularity, SalesBarSeries } from "components/charts";
import { useSalesData } from "contexts/SalesDataContext";
import { formatBucketAxisLabel, formatCompactNumber } from "utils/formatting";
import { applyMarketFocus } from "../../data/applyMarketFocus";
import { mapSalesResponse, type FormatMeta } from "../../data/mapSalesResponse";
import { getEffectiveWindow } from "../../utils/effectiveWindow";
import { EarliestDataPill } from "../EarliestDataPill";
import { MetadataEventOverlay } from "../events";
import { withEmptyEventDays } from "../../data/withEmptyEventDays";
import type { MetadataEvent } from "../../hooks/useMetadataEvents";
import {
  TotalSalesTooltip,
  type TotalSalesTooltipSeries,
} from "./TotalSalesTooltip";
import { TotalSalesFormatTooltip } from "./TotalSalesFormatTooltip";
import { TotalSalesFooter } from "./TotalSalesFooter";
import { TotalSalesChartPanel } from "./TotalSalesChartPanel";

export interface TotalSalesChartProps {
  sales: BookSalesResponse;
  /** Home market first. */
  marketOrder?: string[];
  /** Bundle formats to stack; segment i gets purple shade i. */
  formats?: FormatMeta[];
  granularity: Granularity;
  availableGranularities: Granularity[];
  onGranularityChange: (granularity: Granularity) => void;
  events?: MetadataEvent[];
}

export const TotalSalesChart = ({
  sales,
  marketOrder,
  formats,
  granularity,
  availableGranularities,
  onGranularityChange,
  events = [],
}: TotalSalesChartProps) => {
  const { t } = useTranslation(["productDetails"]);
  const { from, to, firstMarketSaleDate, publishedAt, marketFocus } =
    useSalesData();

  const handleDrill = useMemo(() => {
    const granularityIndex = availableGranularities.indexOf(granularity);
    const finerGranularity =
      granularityIndex > 0
        ? availableGranularities[granularityIndex - 1]
        : undefined;
    return finerGranularity
      ? () => onGranularityChange(finerGranularity)
      : undefined;
  }, [granularity, availableGranularities, onGranularityChange]);

  // Only flag a true gap (data after publication), not windows anchored earlier by Amazon pre-orders.
  const effectiveWindow = getEffectiveWindow({
    firstDataDate: firstMarketSaleDate,
    publishedAt,
    selectedStart: from,
    selectedEnd: to,
  });

  const viewModel = useMemo(
    () =>
      applyMarketFocus(
        mapSalesResponse(sales, marketOrder, formats),
        marketFocus,
      ),
    [sales, marketOrder, formats, marketFocus],
  );

  const chartData = useMemo(
    () =>
      granularity === "daily"
        ? withEmptyEventDays(viewModel.data, events)
        : viewModel.data,
    [viewModel.data, events, granularity],
  );

  const isByFormat = viewModel.stackMode === "format";
  const isByMarket = viewModel.stackMode === "market";

  const marketSeries = useMemo<TotalSalesTooltipSeries[]>(
    () =>
      viewModel.markets.map((meta) => {
        const index = marketOrder?.indexOf(meta.market) ?? 0;
        return {
          key: meta.market,
          label: meta.market,
          currency: meta.currency,
          color: chartSeriesColor(index >= 0 ? index : 0),
          colorClassName: chartSeriesBgClass(index >= 0 ? index : 0),
        };
      }),
    [viewModel.markets, marketOrder],
  );

  const formatSeries = useMemo<TotalSalesTooltipSeries[]>(
    () =>
      (viewModel.formats ?? []).map((meta, index) => ({
        key: meta.isbn,
        label: meta.label,
        currency: "",
        // Darkest at the bottom of the stack, lighter going up.
        color: bundleFormatColor(index),
        colorClassName: bundleFormatBgClass(index),
      })),
    [viewModel.formats],
  );

  const activeSeries = isByFormat ? formatSeries : marketSeries;

  const config = useMemo<ChartConfig>(() => {
    if (isByFormat || isByMarket) {
      return Object.fromEntries(
        activeSeries.map((entry) => [
          entry.key,
          { label: entry.label, color: entry.color },
        ]),
      );
    }
    return {
      total: {
        label: t("productDetails:salesData.totalSales.footer.units"),
        color: CHART_COLORS[0],
      },
    };
  }, [activeSeries, isByFormat, isByMarket, t]);

  const series = useMemo<SalesBarSeries[]>(() => {
    if (isByFormat || isByMarket) {
      const stackId = isByFormat ? "format" : "market";
      return activeSeries.map((entry, index) => ({
        key: entry.key,
        stackId,
        radius:
          index === activeSeries.length - 1
            ? ([4, 4, 0, 0] as [number, number, number, number])
            : undefined,
      }));
    }
    return [{ key: "total", radius: [4, 4, 0, 0] }];
  }, [activeSeries, isByFormat, isByMarket]);

  let tooltipDisplay: "combined" | "byMarket" = "combined";
  if (isByMarket) {
    tooltipDisplay = "byMarket";
  }

  return (
    <TotalSalesChartPanel
      titleBadge={
        effectiveWindow?.dataStartsInWindow ? (
          <EarliestDataPill date={effectiveWindow.earliestDataDate} />
        ) : null
      }
      actions={
        <GranularityToggle
          value={granularity}
          availableGranularities={availableGranularities}
          onChange={onGranularityChange}
        />
      }
      legend={
        isByFormat || isByMarket ? (
          <MarketLegend
            aria-label={t(
              isByFormat
                ? "productDetails:salesData.totalSales.legendFormatsAriaLabel"
                : "productDetails:salesData.totalSales.legendAriaLabel",
            )}
            items={activeSeries.map((entry) => ({
              key: entry.key,
              label: entry.label,
              colorClassName: entry.colorClassName,
              color: entry.color,
            }))}
          />
        ) : undefined
      }
      footer={
        // Format stacks keep combined footer totals; per-bucket split is bars/tooltip only.
        <TotalSalesFooter
          units={viewModel.totals.units}
          revenueByCurrency={viewModel.totals.revenueByCurrency}
          byMarket={viewModel.totals.byMarket}
          display={isByMarket ? "byMarket" : "combined"}
          marketColorClasses={Object.fromEntries(
            marketSeries.flatMap((entry) =>
              entry.colorClassName
                ? [[entry.key, entry.colorClassName] as const]
                : [],
            ),
          )}
        />
      }
    >
      <SalesBarChart
        data={chartData}
        config={config}
        series={series}
        xKey="date"
        xTickFormatter={(value) => formatBucketAxisLabel(value, granularity)}
        yTickFormatter={formatCompactNumber}
        ariaLabel={t("productDetails:salesData.totalSales.chartAriaLabel")}
        tooltipPosition={isByFormat ? "cursor" : "default"}
        tooltipEstimatedWidth={
          isByFormat
            ? estimateFormatTooltipWidth(formatSeries.length)
            : undefined
        }
        tooltipContent={
          isByFormat ? (
            <TotalSalesFormatTooltip
              formats={formatSeries}
              markets={marketSeries}
            />
          ) : (
            <TotalSalesTooltip series={marketSeries} display={tooltipDisplay} />
          )
        }
        overlay={
          <MetadataEventOverlay
            buckets={chartData}
            events={events}
            granularity={granularity}
            onDrill={handleDrill}
          />
        }
      />
    </TotalSalesChartPanel>
  );
};
